import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ id: null, items: [] });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCartId = localStorage.getItem('cartId');
    if (savedCartId) {
      fetchCart(savedCartId);
    }
  }, []);

  const fetchCart = async (cartId) => {
    try {
      const response = await axios.get(`${API}/cart/${cartId}`);
      setCart(response.data);
    } catch (error) {
      // Cart not found, clear localStorage
      localStorage.removeItem('cartId');
      setCart({ id: null, items: [] });
    }
  };

  const addToCart = async (menuItemId, quantity = 1, spiciness = null) => {
    setIsLoading(true);
    try {
      const payload = {
        cart_id: cart.id,
        menu_item_id: menuItemId,
        quantity
      };
      if (spiciness) payload.spiciness = spiciness;
      
      const response = await axios.post(`${API}/cart/add`, payload);
      setCart(response.data);
      localStorage.setItem('cartId', response.data.id);
      // Don't auto-open the cart drawer — the floating cart button shows the count
      // and the customer opens it when ready. Better mobile UX.
      toast.success('Añadido al carrito', { duration: 1600 });
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (menuItemId, quantity) => {
    if (!cart.id) return;
    
    setIsLoading(true);
    try {
      const response = await axios.put(`${API}/cart/${cart.id}/update`, {
        menu_item_id: menuItemId,
        quantity
      });
      setCart(response.data);
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (menuItemId) => {
    if (!cart.id) return;
    
    setIsLoading(true);
    try {
      const response = await axios.delete(`${API}/cart/${cart.id}/item/${menuItemId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = useCallback(async () => {
    if (!cart.id) return;
    
    try {
      await axios.delete(`${API}/cart/${cart.id}`);
      localStorage.removeItem('cartId');
      setCart({ id: null, items: [] });
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  }, [cart.id]);

  const cartTotal = cart.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  const cartItemCount = cart.items.reduce((count, item) => count + item.quantity, 0);

  const value = {
    cart,
    isCartOpen,
    setIsCartOpen,
    isLoading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    cartTotal,
    cartItemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
